/**
 * Shadows are warm-tinted in light mode and doubled in opacity (black-based) in dark mode.
 * `popOffset` is the chunky bottom edge on buttons. It's only the geometry: a custom property's
 * var() references resolve where it's defined (:root), so the color can't live in the token.
 * Components write `box-shadow: var(--gg-shadow-pop-offset) var(--gg-color-action-primary-pop)`.
 */
type Layer = { y: number; blur: number; alpha: number; spread?: number };

const elevation: Record<'sm' | 'md' | 'lg' | 'sheet', Layer[]> = {
  sm: [
    { y: 1, blur: 2, alpha: 0.06 },
    { y: 1, blur: 1, alpha: 0.04 },
  ],
  md: [
    { y: 4, blur: 14, alpha: 0.08 },
    { y: 1, blur: 3, alpha: 0.06 },
  ],
  lg: [
    { y: 14, blur: 36, alpha: 0.14 },
    { y: 2, blur: 6, alpha: 0.06 },
  ],
  sheet: [{ y: -8, blur: 30, alpha: 0.14 }],
};

function render(layers: Layer[], rgb: string, alphaScale: number): string {
  return layers
    .map(
      (l) =>
        `0 ${l.y}px ${l.blur}px ${l.spread ?? 0}px rgba(${rgb}, ${+(l.alpha * alphaScale).toFixed(3)})`,
    )
    .join(', ');
}

export const shadow = {
  light: Object.fromEntries(
    Object.entries(elevation).map(([k, v]) => [k, render(v, '34, 29, 24', 1)]),
  ) as Record<keyof typeof elevation, string>,
  dark: Object.fromEntries(
    Object.entries(elevation).map(([k, v]) => [k, render(v, '0, 0, 0', 2)]),
  ) as Record<keyof typeof elevation, string>,
  /** Theme-independent shadows built from other variables. */
  shared: {
    popOffset: '0 3px 0 0',
    popPressedOffset: '0 1px 0 0',
    /** A 2px separation ring (stacked avatars, badges): `var(--gg-shadow-ring-offset) var(--gg-color-bg-surface)`. */
    ringOffset: '0 0 0 2px',
    focus: '0 0 0 3px var(--gg-color-bg-canvas), 0 0 0 5px var(--gg-color-border-focus)',
  },
} as const;
